import { useEffect, useState } from 'react'
import { Table, Tag, Typography, message } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { dealerBackofficeListBuyerHistoriesOptions, type UserHistoryEntryRoot } from '@repo/sdk'

import { formatKstDateTime } from '../utils/formatKstDateTime'
import { NicknameText } from './NicknameText'
import { Popup } from '@/components/popup'

const PAGE_SIZE = 20

interface BuyerHistoryModalProps {
  buyer: { userId: string; nickname: string }
  open: boolean
  onClose: () => void
}

type Snapshot = Record<string, unknown> | null | undefined

function text(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/** One line for a delivery-address snapshot: recipient · postal code address · phone (masked by the server). */
function formatAddress(snapshot: Snapshot, defaultLabel: string): string {
  if (!snapshot) return '-'
  const parts = [
    text(snapshot['recipientName']),
    [text(snapshot['postalCode']), text(snapshot['addressLine1']), text(snapshot['addressLine2'])]
      .filter(Boolean)
      .join(' '),
    text(snapshot['phoneNumber']),
  ].filter(Boolean)
  const line = parts.join(' · ') || '-'
  return snapshot['isDefault'] === true ? `${line} (${defaultLabel})` : line
}

function isNicknameAction(action: string): boolean {
  return action === 'NICKNAME_CHANGED' || action === 'NICKNAME_FORCE_CHANGED'
}

/**
 * Dealer backoffice: a buyer's nickname and delivery-address change history.
 * The server only answers for buyers the dealer has traded with (403 otherwise).
 */
export function BuyerHistoryModal({ buyer, open, onClose }: BuyerHistoryModalProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (open) setPage(1)
  }, [open])

  const { data, isLoading, error } = useQuery({
    ...dealerBackofficeListBuyerHistoriesOptions({
      path: { buyerId: buyer.userId },
      query: { page, pageSize: PAGE_SIZE },
    }),
    enabled: open,
  })

  useEffect(() => {
    if (error) void message.error(t('common.buyerHistory.failedToLoad'))
  }, [error, t])

  const defaultLabel = t('common.buyerHistory.defaultAddress')

  const columns: ColumnType<UserHistoryEntryRoot>[] = [
    {
      title: t('common.buyerHistory.columns.changedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt: string) => formatKstDateTime(createdAt, 'YYYY-MM-DD HH:mm'),
    },
    {
      title: t('common.buyerHistory.columns.item'),
      key: 'item',
      width: 150,
      render: (_: unknown, record: UserHistoryEntryRoot) => (
        <Tag
          color={
            isNicknameAction(record.action)
              ? 'blue'
              : record.action === 'ORDER_SHIPPING_ADDRESS_CHANGED'
                ? 'orange'
                : 'green'
          }
        >
          {t(`common.buyerHistory.actions.${record.action}`, { defaultValue: record.action })}
        </Tag>
      ),
    },
    {
      title: t('common.buyerHistory.columns.change'),
      key: 'change',
      render: (_: unknown, record: UserHistoryEntryRoot) => {
        const before = record.before as Snapshot

        const after = record.after as Snapshot
        if (isNicknameAction(record.action)) {
          return (
            <span>
              <Typography.Text delete type="secondary">
                {text(before?.['nickname']) || '-'}
              </Typography.Text>
              {' → '}
              <NicknameText nickname={text(after?.['nickname'])}>
                {text(after?.['nickname']) || '-'}
              </NicknameText>
            </span>
          )
        }
        if (record.action === 'DELIVERY_ADDRESS_ADDED') {
          return <span>{formatAddress(after, defaultLabel)}</span>
        }
        if (record.action === 'DELIVERY_ADDRESS_DELETED') {
          return (
            <Typography.Text delete type="secondary">
              {formatAddress(before, defaultLabel)}
            </Typography.Text>
          )
        }
        return (
          <span>
            <Typography.Text delete type="secondary">
              {formatAddress(before, defaultLabel)}
            </Typography.Text>
            <br />
            {'→ '}
            {formatAddress(after, defaultLabel)}
          </span>
        )
      },
    },
  ]

  return (
    <Popup
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={t('common.buyerHistory.title', { nickname: buyer.nickname })}
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('common.buyerHistory.subtitle')}
      </Typography.Paragraph>
      <Table<UserHistoryEntryRoot>
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="historyId"
        loading={isLoading}
        size="small"
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.meta?.totalItems ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        locale={{ emptyText: <div>{t('common.buyerHistory.empty')}</div> }}
      />
    </Popup>
  )
}
